import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaMapMarkerAlt, FaSearch, FaTimes } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";

function normalizeName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function findByName(list, name, key) {
  const target = normalizeName(name);
  if (!target || !Array.isArray(list)) return null;
  return (
    list.find((item) => normalizeName(item[key]) === target) ||
    list.find((item) => {
      const current = normalizeName(item[key]);
      return current.includes(target) || target.includes(current);
    }) ||
    null
  );
}

export async function resolvePlaceDetailsToForm(details) {
  const next = {
    Address_Line_1: details?.addressLine1 || details?.formattedAddress || "",
    Address_Line_2: details?.addressLine2 || "",
    Zip_Code: details?.postalCode || "",
    countryId: "",
    stateId: "",
    cityId: "",
    latitude: details?.latitude || "",
    longitude: details?.longitude || "",
    formattedAddress: details?.formattedAddress || "",
  };

  if (!details?.country) return next;

  try {
    const countriesRes = await axiosInstance.get("/client/countries");
    const countries = Array.isArray(countriesRes.data?.data) ? countriesRes.data.data : [];
    const country = findByName(countries, details.country, "country_name");
    if (!country) return next;
    next.countryId = String(country.country_id);

    if (!details.state) return next;
    const statesRes = await axiosInstance.get(`/client/states/${country.country_id}`);
    const states = Array.isArray(statesRes.data?.data) ? statesRes.data.data : [];
    const state = findByName(states, details.state, "state_name");
    if (!state) return next;
    next.stateId = String(state.state_id);

    if (!details.city) return next;
    const citiesRes = await axiosInstance.get(`/client/cities/${state.state_id}`);
    const cities = Array.isArray(citiesRes.data?.data) ? citiesRes.data.data : [];
    const city = findByName(cities, details.city, "city_name");
    if (city) next.cityId = String(city.city_id);
  } catch (_) {
    // Keep free-text address lines / zip even if master matching fails.
  }

  return next;
}

// Rendered in a fixed layer so cards, scroll areas and modals can't clip or cover the list.
function AddressSuggestions({ search, onSelect }) {
  const { open, loading, error, suggestions, query, wrapRef, menuRef } = search;
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return undefined;
    const place = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < 200 && rect.top > spaceBelow;
      setStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight: Math.min(280, (openUp ? rect.top : spaceBelow) - 8),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, wrapRef]);

  if (!open || !style || query.trim().length < 3) return null;
  const target = wrapRef.current?.closest(".cportal") || document.body;

  let body;
  if (loading) {
    body = <div className="cportal-address-suggestions-status">Searching…</div>;
  } else if (error) {
    body = <div className="cportal-address-suggestions-status is-error">{error}</div>;
  } else if (!suggestions.length) {
    body = <div className="cportal-address-suggestions-status">No matching addresses found</div>;
  } else {
    body = (
      <ul role="listbox">
        {suggestions.map((item) => (
          <li key={item.place_id}>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onSelect(item)}>
              <FaMapMarkerAlt aria-hidden="true" />
              <span>{item.description}</span>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return createPortal(
    <div ref={menuRef} className="cportal-address-suggestions" style={style}>
      {body}
    </div>,
    target
  );
}

function useAddressSearch() {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const requestRef = useRef(0);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (event) => {
      if (wrapRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const search = (value) => {
    setQuery(value);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const trimmed = value.trim();
    const requestId = ++requestRef.current;
    if (trimmed.length < 3) {
      setSuggestions([]);
      setError("");
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setOpen(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get("/client/address-suggestions", {
          params: { address: trimmed },
        });
        if (requestId !== requestRef.current) return;
        setSuggestions(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        if (requestId !== requestRef.current) return;
        setSuggestions([]);
        setError(err.response?.data?.message || "Could not load address suggestions.");
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, 350);
  };

  const clearSuggestions = () => {
    requestRef.current += 1;
    setSuggestions([]);
    setLoading(false);
    setOpen(false);
  };

  return {
    query,
    setQuery,
    suggestions,
    loading,
    error,
    open,
    setOpen,
    wrapRef,
    menuRef,
    search,
    clearSuggestions,
  };
}

function MapPickerModal({ open, onClose, onApply, initialQuery = "" }) {
  const search = useAddressSearch();
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    search.setQuery(initialQuery || "");
    setPreview(null);
    setError("");
    search.clearSuggestions();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  if (!open) return null;

  const selectSuggestion = async (suggestion) => {
    setError("");
    search.setQuery(suggestion.description);
    search.clearSuggestions();
    setApplying(true);
    try {
      const res = await axiosInstance.get("/client/address-details", {
        params: { placeId: suggestion.place_id },
      });
      setPreview(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load place details.");
      setPreview(null);
    } finally {
      setApplying(false);
    }
  };

  const applyPreview = async () => {
    if (!preview) return;
    setApplying(true);
    setError("");
    try {
      const resolved = await resolvePlaceDetailsToForm(preview);
      onApply(resolved);
      onClose();
    } catch (_) {
      setError("Could not apply this address.");
    } finally {
      setApplying(false);
    }
  };

  const mapSrc =
    preview?.latitude && preview?.longitude
      ? `https://maps.google.com/maps?q=${encodeURIComponent(
          `${preview.latitude},${preview.longitude}`
        )}&z=16&output=embed`
      : search.query.trim()
        ? `https://maps.google.com/maps?q=${encodeURIComponent(search.query.trim())}&z=14&output=embed`
        : "https://maps.google.com/maps?q=India&z=5&output=embed";

  return (
    <div className="cportal-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cportal-modal cportal-modal--wide cportal-map-picker-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-picker-title"
      >
        <div className="cportal-modal-header">
          <h3 id="map-picker-title">Select Address from Map</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="cportal-map-picker-body">
          <div className="cportal-address-search" ref={search.wrapRef}>
            <FaSearch className="cportal-address-search-icon" aria-hidden="true" />
            <input
              type="text"
              value={search.query}
              onChange={(e) => search.search(e.target.value)}
              onFocus={() => search.query.trim().length >= 3 && search.setOpen(true)}
              placeholder="Search Google Maps address"
              autoComplete="off"
            />
            <AddressSuggestions search={search} onSelect={selectSuggestion} />
          </div>

          <p className="cportal-map-picker-hint">
            Search and pick a place to preview it on the map, then use this address.
          </p>
          {error ? <div className="cportal-alert cportal-alert-error">{error}</div> : null}

          <div className="cportal-map-picker-frame-wrap">
            <iframe title="Select location map" src={mapSrc} className="cportal-map-picker-frame" />
          </div>

          {preview ? (
            <div className="cportal-map-picker-preview">
              <strong>{preview.formattedAddress || preview.addressLine1 || "Selected location"}</strong>
              <span>
                {[preview.addressLine1, preview.city, preview.state, preview.country, preview.postalCode]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          ) : null}

          <div className="cportal-form-actions">
            <button type="button" className="cportal-btn cportal-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="cportal-btn cportal-btn-primary"
              onClick={applyPreview}
              disabled={!preview || applying}
            >
              {applying ? "Applying…" : "Use This Address"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PortalAddressPicker({ onResolved, initialQuery = "" }) {
  const search = useAddressSearch();
  const [mapOpen, setMapOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");

  const applyDetails = async (details) => {
    const resolved = await resolvePlaceDetailsToForm(details);
    onResolved?.(resolved);
  };

  const selectSuggestion = async (suggestion) => {
    setError("");
    search.setQuery(suggestion.description);
    search.clearSuggestions();
    setResolving(true);
    try {
      const res = await axiosInstance.get("/client/address-details", {
        params: { placeId: suggestion.place_id },
      });
      await applyDetails(res.data?.data || {});
    } catch (err) {
      setError(err.response?.data?.message || "Could not load address details.");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="cportal-address-picker">
      <div className="cportal-address-picker-toolbar">
        <div className="cportal-address-search cportal-address-search--toolbar" ref={search.wrapRef}>
          <FaSearch className="cportal-address-search-icon" aria-hidden="true" />
          <input
            type="text"
            value={search.query}
            onChange={(e) => search.search(e.target.value)}
            onFocus={() => search.query.trim().length >= 3 && search.setOpen(true)}
            placeholder="Search address with Google Maps"
            autoComplete="off"
            disabled={resolving}
          />
          <AddressSuggestions search={search} onSelect={selectSuggestion} />
        </div>
        <button
          type="button"
          className="cportal-btn cportal-btn-edit"
          onClick={() => setMapOpen(true)}
        >
          <FaMapMarkerAlt aria-hidden="true" />
          <span className="cportal-btn-text">Select from Map</span>
        </button>
      </div>
      {error ? <div className="cportal-alert cportal-alert-error">{error}</div> : null}
      {resolving ? <p className="cportal-muted">Filling address fields…</p> : null}

      <MapPickerModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onApply={(resolved) => onResolved?.(resolved)}
        initialQuery={search.query || initialQuery}
      />
    </div>
  );
}

export default PortalAddressPicker;
